import { PopApplication } from "../models"
import { Firestore, QueryConstraint, QueryFieldFilterConstraint, collection, documentId, orderBy, query, where } from "@angular/fire/firestore"
import { toUniqueNonNullableArray } from "@nx-superprep/utils"
import { chunk } from "lodash-es"


/**
 * Constructs a Firestore query with flexible options for document filtering, sorting,
 * and enforcing role-based application availability.
 *
 * @param firestore - The Firestore instance used to create the query.
 * @param collectionPath - The Firestore collection path from which documents are retrieved.
 * @param options - The query options object.
 *
 *   - `names` (optional): A string or array of strings representing document IDs to filter.
 *   - `application` (optional): A string representing the application used for availability filtering.
 *   - `sorted` (optional): A boolean to indicate whether the result should be sorted by `title`.
 *   - `filters` (optional): An array of `QueryFieldFilterConstraint` for applying specific field-based filters.
 *   - `nonFilters` (optional): An array of `QueryConstraint` for additional query constraints (e.g., sorting).
 *   - `isContentManager` (optional): A boolean flag indicating if the user is a content manager.
 *       If true, the user bypasses application-based filtering.
 *
 * @param application - The default application for filtering based on `appAvailability`. Defaults to `PopApplication.LT_PROD_WEB`.
 *
 * @returns A Firestore `Query` object, or the Firestore `CollectionReference` if no query constraints are provided.
 */
export function setQuery(
  firestore: Firestore,
  collectionPath: string,
  options: {
    names?: string | (string | null | undefined)[],
    application?: string
    sorted?: boolean
    filters?: QueryFieldFilterConstraint[]
    nonFilters?: QueryConstraint[]
    isContentManager?: boolean
  },
  application: PopApplication = PopApplication.LT_PROD_WEB
) {
  // Array to hold the query constraints
  const qConstraints: QueryConstraint[] = []
  // Convert 'names' to a unique non-nullable array, to be used for document ID filtering
  const names = toUniqueNonNullableArray(options?.names)
  if (names.length > 0) {
    const qs = chunk(names, 30).map(chunk => where(documentId(), 'in', chunk))
    qConstraints.push(...qs)
  }
  // enforce role based value for application availability filter when one is provided
  if (!options.isContentManager && options.application) { options.application = application}
  // Add a filter for app availability based on the application, if provided
  if (options?.application) { qConstraints.push(where(`appAvailability.${options.application}`, '==', true))}
  // Add any additional field filters to the query constraints
  if (options.filters) { qConstraints.push(...options.filters)}
  // Add any non-field constraints (e.g., sorting)
  if (options.nonFilters) { qConstraints.push(...options.nonFilters)}
  // If sorting by title is requested, add an orderBy constraint
  if (options.sorted) { qConstraints.push(orderBy('title'))}
  // Get the reference to the Firestore collection
  const ref = collection(firestore, collectionPath)
  // Return the query with constraints if any, otherwise return the collection reference
  return qConstraints.length > 0 ? query(ref, ...qConstraints) : ref
}
